import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubscriptionDto } from '@/Domain/api/Subscription/dto/Subscription.dto';
import { SubscriptionDraftDto } from '@/Domain/api/Subscription/dto/SubscriptionDraft.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { BillingSection } from '@/Domain/components/Subscription/ChangePlanDialog/BillingSection';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { SubscriptionHelper } from '@/Domain/helper/Subscription.helper';

type Props = {
  data: SubscriptionDraftDto | undefined;
  isOpen: boolean;
  isPending: boolean;
  onOpenChange: () => void;
  onCallback: () => void;
  currentPlan: SubscriptionDto['current_plan'];
  nextTier: TierEnum;
  interval: SubscriptionIntervalEnum;
  canceled?: boolean;
};

export const ChangePlanDialog = ({
  data,
  isOpen,
  isPending,
  onOpenChange,
  onCallback,
  nextTier,
  currentPlan,
  interval,
  canceled,
}: Props) => {
  const { t } = useTranslation('subscription_page');

  const [isActionPending, setIsActionPending] = useState(false);

  const handleCallback = () => {
    setIsActionPending(true);

    onCallback();
  };

  useEffect(() => {
    if (!isOpen) {
      setIsActionPending(false);
    }
  }, [isOpen]);

  return (
    <Dialog
      open={isActionPending ? true : isOpen}
      onOpenChange={() => !isActionPending && onOpenChange()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('change_plan_dialog.title')}</DialogTitle>
          <DialogDescription>{t('change_plan_dialog.description')}</DialogDescription>
        </DialogHeader>
        <Separator />
        <div className="text-sm space-y-4">
          <h5 className="text-sm text-foreground-subtle font-medium">
            {t('change_plan_dialog.selected_subscription')}
          </h5>
          <div className="flex flex-col gap-3 py-2">
            <section className="flex items-center justify-between">
              <p className="text-foreground-subtle">{t('change_plan_dialog.current_plan')}</p>
              <p className="text-foreground font-medium">
                {SubscriptionHelper.getTrierLabel(currentPlan.tier, undefined, canceled)}{' '}
                {SubscriptionHelper.getIntervalLabel(currentPlan.interval)}
              </p>
            </section>
            <section className="flex items-center justify-between">
              <p className=" text-foreground-subtle">{t('change_plan_dialog.new_plan')}</p>
              <p className="text-foreground font-medium">
                {SubscriptionHelper.getTrierLabel(nextTier)}{' '}
                {SubscriptionHelper.getIntervalLabel(interval)}
              </p>
            </section>
          </div>
          <Separator />
          <h5 className=" text-foreground-subtle font-medium">{t('change_plan_dialog.billing')}</h5>
          <BillingSection data={data} isPending={isPending} />
        </div>
        <Separator />
        <DialogFooter>
          <DialogClose>
            <Button variant="secondary" disabled={isActionPending}>
              {t('change_plan_dialog.button.cancel')}
            </Button>
          </DialogClose>
          <Button onClick={handleCallback} disabled={isPending || isActionPending}>
            {isActionPending && <Spinner className="text-white h-4" />}
            {isActionPending
              ? t('change_plan_dialog.button.processing')
              : t('change_plan_dialog.button.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
